import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API_URL, CP_API_URL } from "../../config/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import "../../styles/derma.css";

/**
 * Borang sumbangan KEMPEN untuk penyumbang daripada aplikasi mudah alih —
 * kembar Checkout.jsx institusi, tetapi sasarannya satu kempen tertentu:
 * tiada pemilihan tabung, dan kepala halaman memaparkan progres kempen.
 *
 * Garis panduan App Store 3.2.2(iv) menghendaki kutipan derma berlaku DI LUAR
 * aplikasi — aplikasi membuka halaman ini melalui /api/derma/handoff dengan
 * campaignCode, dan token ?h= mengisi maklumat penyumbang secara automatik.
 * Token tidak pernah wajib; lihat nota di Checkout.jsx.
 */

const AMAUN_PANTAS = [10, 20, 50, 100];

function ringgit(n) {
    const v = Number(n);
    if (!Number.isFinite(v)) return "0.00";
    // Pemisah ribu — RM1000000.00 tanpa koma melimpah keluar kad dan
    // memecahkan baris pada skrin kecil.
    return v.toLocaleString("ms-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const KempenCheckout = () => {
    const { campaignCode } = useParams();
    const [searchParams] = useSearchParams();
    const tokenHandoff = searchParams.get("h");

    const [memuat, setMemuat] = useState(true);
    const [ralatMuat, setRalatMuat] = useState(null);

    const [kempen, setKempen] = useState(null);
    const [institusi, setInstitusi] = useState(null);
    const [amaunMinimum, setAmaunMinimum] = useState(2);

    const [amaun, setAmaun] = useState("");
    const [nama, setNama] = useState("");
    const [emel, setEmel] = useState("");
    const [telefon, setTelefon] = useState("");
    const [accountId, setAccountId] = useState(null);

    const [menghantar, setMenghantar] = useState(false);
    const [ralatHantar, setRalatHantar] = useState(null);

    const asasWeb = useMemo(() => String(API_URL).replace(/\/api\/?$/, ""), []);

    useEffect(() => {
        let dibatalkan = false;

        const muat = async () => {
            try {
                const [rKempen, rKonfig, rHandoff] = await Promise.all([
                    fetch(`${asasWeb}/web/derma/kempen/${encodeURIComponent(campaignCode)}`).then((r) => r.json()),
                    fetch(`${asasWeb}/web/derma/konfig`).then((r) => r.json()),
                    tokenHandoff
                        ? fetch(`${asasWeb}/web/derma/handoff/${encodeURIComponent(tokenHandoff)}`).then((r) => r.json())
                        : Promise.resolve(null),
                ]);

                if (dibatalkan) return;

                if (rKempen?.status !== 200 || !rKempen?.data) {
                    setRalatMuat(rKempen?.message || "Kempen tidak dijumpai.");
                    return;
                }

                setKempen(rKempen.data.kempen);
                setInstitusi(rKempen.data.institusi);

                if (rKonfig?.data?.amaun_minimum) setAmaunMinimum(Number(rKonfig.data.amaun_minimum));

                if (rHandoff?.data) {
                    setNama(rHandoff.data.nama || "");
                    setEmel(rHandoff.data.emel || "");
                    setTelefon(rHandoff.data.telefon || "");
                    setAccountId(rHandoff.data.account_id ?? null);
                }
            } catch (e) {
                if (!dibatalkan) setRalatMuat("Tidak dapat menghubungi pelayan. Sila cuba lagi.");
            } finally {
                if (!dibatalkan) setMemuat(false);
            }
        };

        muat();
        return () => { dibatalkan = true; };
    }, [asasWeb, campaignCode, tokenHandoff]);

    const amaunNombor = parseFloat(amaun);
    const amaunSah = Number.isFinite(amaunNombor) && amaunNombor >= amaunMinimum;
    const bolehHantar = amaunSah && nama.trim() && emel.trim() && telefon.trim() && !menghantar;

    const progres = useMemo(() => {
        if (!kempen || !kempen.sasaran) return 0;
        return Math.min(100, Math.round((kempen.dikutip / kempen.sasaran) * 100));
    }, [kempen]);

    const hariBerbaki = useMemo(() => {
        if (!kempen?.tarikh_akhir) return null;
        const beza = Math.ceil((new Date(kempen.tarikh_akhir) - new Date()) / 86400000);
        return beza > 0 ? beza : 0;
    }, [kempen]);

    const [tentangPenuh, setTentangPenuh] = useState(false);

    const hantar = async () => {
        setRalatHantar(null);
        setMenghantar(true);

        try {
            const res = await fetch(`${CP_API_URL}/createPayment`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    isCampaign: true,
                    orgCode: institusi?.kod,
                    campaignCode,
                    amount: amaunNombor.toFixed(2),
                    payorName: nama.trim(),
                    payorEmail: emel.trim(),
                    payorPhone: telefon.trim(),
                    paymentChannel: "1",
                    userId: accountId,
                }),
            });
            const json = await res.json();

            // Laluan ini memulangkan HTTP 200 walaupun untuk kegagalan.
            if (json?.status === 200 && json?.data) {
                const asasGerbang =
                    process.env.REACT_APP_ENV === "production"
                        ? "https://toyyibpay.com/"
                        : "https://dev.toyyibpay.com/";
                window.location.href = asasGerbang + json.data;
                return;
            }

            setRalatHantar(json?.message || "Sumbangan tidak dapat diproses. Sila cuba lagi.");
        } catch (e) {
            setRalatHantar("Tidak dapat menghubungi pelayan. Sila semak sambungan internet anda.");
        } finally {
            setMenghantar(false);
        }
    };

    if (memuat) {
        return (
            <div className="derma-root">
                <div className="flex min-h-screen items-center justify-center px-6">
                    <p className="text-sm text-muted-foreground">Memuatkan…</p>
                </div>
            </div>
        );
    }

    if (ralatMuat) {
        return (
            <div className="derma-root">
                <div className="mx-auto flex min-h-screen max-w-md items-center px-6">
                    <Alert variant="destructive">
                        <AlertDescription>{ralatMuat}</AlertDescription>
                    </Alert>
                </div>
            </div>
        );
    }

    return (
        <div className="derma-root">
            {/* Reka bentuk benchmark pemilik: hero penuh + helaian bertindih +
                amaun besar di tengah + CTA melekat. Tiada tag heading — tema
                global laman memaksa saiz gergasi padanya (lihat sejarah git). */}

            {/* ── Hero penuh ── */}
            <div className="relative">
                {(kempen.imej || institusi?.imej) ? (
                    <img src={kempen.imej || institusi.imej} alt=""
                        className="h-72 w-full object-cover" />
                ) : (
                    <div className="flex h-72 w-full items-center justify-center bg-primary/10">
                        <span className="text-lg font-bold text-primary">Al-Jariyah</span>
                    </div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/30 to-transparent" />
            </div>

            {/* ── Helaian bertindih ── */}
            <div className="relative z-10 -mt-10 rounded-t-3xl bg-background">
                <div className="mx-auto max-w-md px-5 pb-36 pt-6">

                    <div className="text-xl font-bold leading-snug text-foreground">{kempen.tajuk}</div>
                    <div className="mt-3 flex items-center gap-2.5">
                        {institusi?.imej ? (
                            <img src={institusi.imej} alt=""
                                className="h-8 w-8 rounded-full border border-border object-cover" />
                        ) : (
                            <div className="h-8 w-8 rounded-full bg-primary/10" />
                        )}
                        <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-foreground">{institusi?.nama}</div>
                            <div className="text-[11px] text-muted-foreground">Penganjur kempen</div>
                        </div>
                    </div>

                    {/* Progres */}
                    <div className="mt-5 rounded-2xl border border-border bg-card p-4 shadow-sm">
                        <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                            <div className="h-2.5 rounded-full bg-primary" style={{ width: `${progres}%` }} />
                        </div>
                        <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                            <div className="min-w-0 text-sm">
                                <span className="font-bold text-foreground">RM {ringgit(kempen.dikutip)}</span>
                                <span className="text-xs text-muted-foreground"> drpd RM {ringgit(kempen.sasaran)}</span>
                            </div>
                            {hariBerbaki !== null && (
                                <span className="whitespace-nowrap text-xs font-semibold text-muted-foreground">
                                    {hariBerbaki > 0 ? `${hariBerbaki} hari lagi` : "Hari terakhir"}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Tentang — dipotong dengan "Lihat lagi" */}
                    {kempen.kandungan && (
                        <div className="mt-5">
                            <div className="text-sm font-bold text-foreground">Tentang Kempen</div>
                            <div className={`derma-kandungan mt-1.5 overflow-hidden text-sm leading-relaxed text-muted-foreground ${tentangPenuh ? "" : "max-h-24"}`}
                                dangerouslySetInnerHTML={{ __html: kempen.kandungan }} />
                            <button type="button" onClick={() => setTentangPenuh(!tentangPenuh)}
                                className="mt-1 text-xs font-bold text-primary">
                                {tentangPenuh ? "Tutup" : "Lihat lagi"}
                            </button>
                        </div>
                    )}

                    {/* Amaun — besar di tengah, gaya benchmark */}
                    <div className="mt-6">
                        <div className="text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Jumlah Sumbangan
                        </div>
                        <div className="mt-2 flex items-baseline justify-center gap-1.5 border-b border-border pb-3">
                            <span className="text-lg font-bold text-muted-foreground">RM</span>
                            <input type="number" inputMode="decimal" value={amaun}
                                onChange={(e) => setAmaun(e.target.value)}
                                placeholder="0"
                                className="w-44 border-0 bg-transparent text-center text-5xl font-bold text-foreground placeholder:text-muted-foreground focus:outline-none" />
                        </div>
                        <div className="mx-auto mt-4 grid max-w-xs grid-cols-4 gap-2">
                            {AMAUN_PANTAS.map((n) => (
                                <button key={n} type="button" onClick={() => setAmaun(String(n))}
                                    className={`rounded-full border py-2 text-center text-sm font-bold transition-colors ${
                                        Number(amaun) === n
                                            ? "border-primary bg-primary text-white"
                                            : "border-border bg-card text-foreground"
                                    }`}>
                                    {n}
                                </button>
                            ))}
                        </div>
                        {amaun && !amaunSah && (
                            <div className="mt-2 text-center text-xs text-red-600">
                                Jumlah minimum ialah RM {ringgit(amaunMinimum)}.
                            </div>
                        )}
                    </div>

                    {/* Maklumat penyumbang */}
                    <div className="mt-6 rounded-2xl border border-border bg-card p-4 shadow-sm">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Maklumat Penyumbang
                        </div>
                        <div className="mt-2.5 space-y-3">
                            <input value={nama} onChange={(e) => setNama(e.target.value)}
                                placeholder="Nama penuh"
                                className="h-12 w-full rounded-xl border border-input bg-white px-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                            <input type="email" value={emel} onChange={(e) => setEmel(e.target.value)}
                                placeholder="E-mel"
                                className="h-12 w-full rounded-xl border border-input bg-white px-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                            <input type="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)}
                                placeholder="No. telefon"
                                className="h-12 w-full rounded-xl border border-input bg-white px-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                        </div>
                        <div className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                            Resit rasmi akan dihantar ke e-mel anda selepas pembayaran berjaya.
                        </div>
                    </div>

                    {ralatHantar && (
                        <Alert variant="destructive" className="mt-4">
                            <AlertDescription>{ralatHantar}</AlertDescription>
                        </Alert>
                    )}
                </div>
            </div>

            {/* ── CTA melekat ── */}
            <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background/95 backdrop-blur">
                <div className="mx-auto max-w-md px-5 pb-6 pt-3">
                    <button type="button" onClick={hantar} disabled={!bolehHantar}
                        className="h-12 w-full rounded-xl bg-primary text-base font-bold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50">
                        {menghantar ? "Memproses…" : amaunSah ? `Sumbang RM ${ringgit(amaunNombor)}` : "Sumbang Sekarang"}
                    </button>
                    <div className="mt-2 text-center text-[11px] text-muted-foreground">
                        Pembayaran selamat melalui ToyyibPay (FPX)
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KempenCheckout;
